import type { TOptions, TPlayer } from 'video.js'
import type { CmcdPluginOptions } from './plugin'

import {
  getPlaybackSessionId,
  setPlaybackSessionId,
} from '|>/core/addons/playback-session'
import { Events } from '|>/shared/events'
import { createLogger } from '|>/shared/logger'
import './plugin'

// re-export plugin options and plugin class (as a type)
export type { CmcdPlugin, CmcdPluginOptions } from './plugin'

/**
 * CMCD (Common Media Client Data) configuration
 */
export type CmcdConfiguration = CmcdPluginOptions & {
  enabled?: boolean
}

/**
 * Key to store CMCD configuration in player options
 */
const cmcdKey = '~~cmcd'
const logger = createLogger('addons/cmcd')

/**
 * Check if CMCD is enabled for the player
 */
export function isCmcdEnabled(player: TPlayer): boolean {
  const cfg = player.options_[cmcdKey] || {}
  return Boolean(cfg.enabled)
}

/**
 * Configure CMCD plugin before player initialization
 */
export function configure(configuration?: CmcdConfiguration) {
  return (options: TOptions): TOptions => {
    // store configuration in player options
    options[cmcdKey] = configuration || {}

    return options
  }
}

/**
 * Injects CMCD plugin and functionality into the player
 */
export function inject(player: TPlayer): TPlayer {
  const onPlaybackSessionId = (_: unknown, sid?: string) => {
    if (!sid) return
    player.options_[cmcdKey] = player.options_[cmcdKey] || {}
    const currentSid = player.options_[cmcdKey].sid
    if (currentSid === sid) return
    player.options_[cmcdKey].sid = sid
    logger.debug('pureya/cmcd playbackSessionId -> cmcd sid', { sid })
    if (!isCmcdEnabled(player)) return
    try {
      const cmcd = player.cmcd()
      if (cmcd.sid !== sid) {
        cmcd.setId({ sid })
        logger.debug('pureya/cmcd setId from playbackSessionId', { sid })
      }
    } catch (ignore) {}
  }

  // update configuration on player configuration update
  player.on(
    Events.Player.UpdateConfiguration,
    (_, configuration?: { cmcd?: CmcdConfiguration }) => {
      const previousCfg = player.options_[cmcdKey] || {}
      const cfg = configuration?.cmcd || {}
      const playbackSid = getPlaybackSessionId(player)

      if (cfg.sid && cfg.sid !== playbackSid) {
        setPlaybackSessionId(player, cfg.sid)
        logger.debug('pureya/cmcd config sid overrides playbackSessionId', {
          sid: cfg.sid,
        })
      } else if (!cfg.sid && playbackSid) {
        cfg.sid = playbackSid
        logger.debug('pureya/cmcd config sid from playbackSessionId', {
          sid: cfg.sid,
        })
      }

      // if CMCD was enabled before
      if (previousCfg.enabled) {
        // update cid/sid
        if (cfg.enabled) {
          player.cmcd().setId(cfg)
        }

        // disable it
        else {
          player.cmcd().disable()
          player.cmcd().dispose()
        }
      }

      // if CMCD was disabled before
      else {
        // enable it
        if (cfg.enabled) {
          player.cmcd(cfg)
          player.cmcd().enable()
          logger.debug('pureya/cmcd enabled', { sid: cfg.sid })
        }
      }

      // update configuration in player options
      player.options_[cmcdKey] = cfg
    }
  )

  player.on(Events.Media.PlaybackSessionId, onPlaybackSessionId)
  player.on('dispose', () => {
    player.off(Events.Media.PlaybackSessionId, onPlaybackSessionId)
  })

  // enable CMCD right away if it's enabled in player configuration
  const cfg = player.options_[cmcdKey] || {}
  if (cfg.enabled) {
    const playbackSid = getPlaybackSessionId(player)
    if (cfg.sid && cfg.sid !== playbackSid) {
      setPlaybackSessionId(player, cfg.sid)
      logger.debug('pureya/cmcd init sid overrides playbackSessionId', {
        sid: cfg.sid,
      })
    } else if (!cfg.sid && playbackSid) {
      cfg.sid = playbackSid
      logger.debug('pureya/cmcd init sid from playbackSessionId', {
        sid: cfg.sid,
      })
    }
    player.cmcd(cfg)
    logger.debug('pureya/cmcd init enabled', { sid: cfg.sid })
  }

  return player
}
